<!-- eslint-disable no-undef -->
<template>
  <div class="app-container">
    <Header />

    <!-- Main Content -->
    <main class="container">
      <div class="left-container">
        <div class="listing-details">
          <div class="event-form">
            <h2>{{ isEditMode ? "Edit Event" : "Create New Event" }}</h2>
            <h6 class="subheading">Tell us about your event</h6>

            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="title">Title</label>
                <input
                  type="text"
                  id="title"
                  v-model="event.title"
                  placeholder="Enter title"
                  required
                />
              </div>

              <div class="form-group">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  v-model="event.description"
                  placeholder="Provide description"
                  required
                ></textarea>
              </div>

              <div class="form-group">
                <label for="location">Location</label>
                <gmp-place-autocomplete
                  ref="autocompleteInput"
                  id="location"
                  placeholder="Lake Shoji, Yamanashi, Japan"
                  @gmp-select="handlePlaceSelect"
                ></gmp-place-autocomplete>
              </div>

              <div class="form-group">
                <label for="timezone">Event Timezone</label>
                <select
                  id="timezone"
                  v-model="event.timezone"
                  class="timezone-select"
                  @change="handleTimezoneChange"
                >
                  <option v-for="tz in timezones" :key="tz" :value="tz">
                    {{ formatTimezone(tz) }}
                  </option>
                </select>
                <p class="timezone-info">
                  <vue-feather type="clock" size="14"></vue-feather>
                  Event times will be converted to the user's timezone
                </p>
              </div>

              <div class="form-group">
                <label for="date">Date</label>
                <input
                  type="date"
                  v-model="startDate"
                  required
                  class="date-input"
                />
              </div>

              <div class="form-group">
                <label for="time">Time Slots</label>
                <select
                  id="time"
                  v-model="selectedTimeSlot"
                  @change="addTimeSlot"
                  class="time-select"
                >
                  <option disabled value="">Add slots</option>
                  <option v-for="time in timeSlots" :key="time" :value="time">
                    {{ time.timeString }}
                  </option>
                </select>
                <div class="selected-slots">
                  <Chip
                    v-for="slot in selectedSlots"
                    :key="slot"
                    class="time-chip"
                  >
                    {{ slot.timeString }}
                    <button @click="removeTimeSlot(slot)" class="remove-chip">
                      <vue-feather type="x" size="16"></vue-feather>
                    </button>
                  </Chip>
                </div>
              </div>

              <div class="form-group repeat-group">
                <div class="repeat-header">
                  <label>Repeat Event</label>
                  <input
                    type="checkbox"
                    v-model="event.repeat"
                    class="repeat-checkbox"
                  />
                </div>

                <div v-if="event.repeat" class="repeat-options">
                  <div class="frequency-select">
                    <label for="frequency">Event Frequency</label>
                    <select id="frequency" v-model="event.frequency">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>

                  <div class="days-select">
                    <label>Select Days of the Week</label>
                    <div class="days">
                      <div
                        v-for="day in daysOfWeek"
                        :key="day"
                        class="day-option"
                      >
                        <input
                          type="checkbox"
                          :id="day"
                          :value="day"
                          v-model="event.selectedDays"
                        />
                        <label :for="day">{{ day }}</label>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="endDate">End Date</label>
                    <input
                      type="date"
                      v-model="endDate"
                      required
                      class="date-input"
                    />
                  </div>
                </div>
              </div>

              <div class="form-group banner-group">
                <div class="banner-header">
                  <label for="banner">Add Banner</label>
                  <button
                    v-if="event.bannerUrl"
                    @click="removeBanner"
                    class="remove-banner"
                    type="button"
                  >
                    <vue-feather type="x" size="16"></vue-feather>
                  </button>
                </div>

                <div
                  v-if="uploadProgress > 0 && uploadProgress < 100"
                  class="upload-progress"
                >
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      :style="{ width: uploadProgress + '%' }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ uploadProgress }}%</span>
                </div>

                <div v-if="event.bannerUrl" class="banner-preview">
                  <img :src="event.bannerUrl" alt="Banner preview" />
                </div>

                <div v-else class="file-input-wrapper">
                  <input
                    type="file"
                    id="bannerUrl"
                    @change="handleFileUpload"
                    accept="image/*"
                    class="file-input"
                  />
                  <div class="file-input-placeholder">
                    <vue-feather type="image" size="24"></vue-feather>
                    <span>Choose File</span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="maxGuestCount">Max Guests Per Slot</label>
                <input
                  type="number"
                  id="maxGuestCount"
                  v-model.number="event.maxGuestCount"
                  min="1"
                  max="20"
                  placeholder="Enter max guests per slot"
                  required
                />
              </div>

              <div class="form-group privacy-group">
                <div class="privacy-header">
                  <label>Make Event Private</label>
                  <input
                    type="checkbox"
                    v-model="event.isPrivate"
                    class="privacy-checkbox"
                  />
                </div>
                <p class="privacy-info">
                  <vue-feather type="eye-off" size="14"></vue-feather>
                  Private events won't be visible on the home page
                </p>
              </div>

              <button type="submit" class="submit-button">
                {{ isEditMode ? "Update Event" : "Create Event" }}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div class="right-container">
        <div class="booking-container">
          <div class="booking-header">
            <h3>Event Schedule</h3>
          </div>

          <div class="calendar-wrapper">
            <VCalendar
              expanded
              :attributes="attrs"
              title-position="left"
              borderless
              transparent
              :min-date="new Date()"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- Debug Modal for Event Schedule -->
    <div class="debug-modal" v-if="showDebugModal">
      <div class="debug-modal-content">
        <div class="debug-modal-header">
          <h3>Event Schedule Debug</h3>
          <button @click="showDebugModal = false" class="close-modal-button">
            <vue-feather type="x" size="20"></vue-feather>
          </button>
        </div>
        <div class="debug-modal-body">
          <div class="schedule-info">
            <p>Total Events: {{ eventSchedule.length }}</p>
            <p>
              Date Range: {{ startDate }} to
              {{ event.repeat ? endDate : startDate }}
            </p>
            <p>Selected Time Slots: {{ selectedSlots.length }}</p>
          </div>

          <!-- Original UTC Table -->
          <h4 class="table-title">Original Schedule (UTC)</h4>
          <div class="schedule-table">
            <table>
              <thead>
                <tr>
                  <th>Slot Id</th>
                  <th>Date (UTC)</th>
                  <th>Time (UTC)</th>
                  <th>Available</th>
                  <th>Locked</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(event, index) in eventSchedule" :key="index">
                  <td>{{ event.slotId.substring(0, 8) }}...</td>
                  <td>{{ formatUTCDate(event.datetime) }}</td>
                  <td>{{ formatUTCTime(event.datetime) }}</td>
                  <td>{{ event.isAvailable ? "Yes" : "No" }}</td>
                  <td>{{ event.isLocked ? "Yes" : "No" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Timezone Conversion Test Section -->
          <div class="timezone-test-section">
            <h4 class="table-title">Timezone Conversion Test</h4>
            <div class="timezone-selector-wrapper">
              <label for="testTimezone">Convert schedule to timezone:</label>
              <select
                v-model="testTimezone"
                id="testTimezone"
                class="timezone-select"
              >
                <option v-for="tz in timezones" :key="tz" :value="tz">
                  {{ formatTimezone(tz) }}
                </option>
              </select>
            </div>

            <div class="schedule-table">
              <table>
                <thead>
                  <tr>
                    <th>Slot Id</th>
                    <th>Date ({{ testTimezone }})</th>
                    <th>Time ({{ testTimezone }})</th>
                    <th>Available</th>
                    <th>Locked</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(event, index) in convertedTimezoneSchedule"
                    :key="index"
                  >
                    <td>{{ event.slotId.substring(0, 8) }}...</td>
                    <td>{{ event.localDate }}</td>
                    <td>{{ event.localTime }}</td>
                    <td>{{ event.isAvailable ? "Yes" : "No" }}</td>
                    <td>{{ event.isLocked ? "Yes" : "No" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="debug-actions">
            <button @click="showDebugModal = false" class="modal-button">
              Close
            </button>
            <button @click="proceedWithSubmission" class="modal-button submit">
              Proceed with Submission
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { db } from "../plugins/firebase";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { storage } from "../plugins/firebase";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { useSessionStore } from "../stores/session";
import Header from "@/components/organisms/Header.vue";
import Chip from "primevue/chip";
import { DateTime } from "luxon";
import { useRoute } from "vue-router";

export default {
  data() {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      isEditMode: false,
      listingId: null,
      uploadProgress: 0,
      isLoading: false,
      event: {
        title: "",
        description: "",
        location: "",
        latitude: "",
        longitude: "",
        repeat: false,
        frequency: "",
        selectedDays: [],
        slotsByDate: new Map(),
        bannerUrl: null,
        timezone: userTimezone,
        isActive: false,
        isPrivate: false,
        maxGuestCount: 4,
      },
      startDate: "",
      endDate: "",
      selectedTimeSlot: "",
      selectedSlots: [],
      eligibleDays: [],
      timeSlots: [],
      timezones: Intl.supportedValuesOf("timeZone"),
      daysOfWeek: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      eventSchedule: [],
      showDebugModal: false,
      testTimezone: userTimezone,
    };
  },
  components: {
    Header,
    Chip,
  },
  created() {
    this.timeSlots = this.generateTimeSlots();

    // Check if we're in edit mode
    const route = useRoute();
    if (route.query.edit === "true" && route.query.listingId) {
      this.isEditMode = true;
      this.listingId = route.query.listingId;
      this.loadListingData();
    }
  },
  methods: {
    // Add method to load existing listing data
    async loadListingData() {
      try {
        this.isLoading = true;
        const listingRef = doc(db, "listings", this.listingId);
        const listingDoc = await getDoc(listingRef);

        if (listingDoc.exists()) {
          const listingData = listingDoc.data();

          // Populate form fields with existing data
          this.event.title = listingData.title || "";
          this.event.description = listingData.description || "";
          this.event.location = listingData.location || "";
          this.event.latitude = listingData.latitude || "";
          this.event.longitude = listingData.longitude || "";
          this.event.repeat = listingData.repeat || false;
          this.event.frequency = listingData.frequency || "";
          this.event.selectedDays = listingData.selectedDays || [];
          this.event.bannerUrl = listingData.bannerUrl || null;
          this.event.timezone = listingData.timezone || this.event.timezone;
          this.event.isActive = listingData.isActive || false;
          this.event.isPrivate = listingData.isPrivate || false;
          this.event.maxGuestCount = listingData.maxGuestCount || 4;

          // Handle date-related fields
          this.startDate = listingData.startDate || "";
          this.endDate = listingData.endDate || "";

          // Handle slot-related fields
          if (listingData.slotsByDate) {
            // Convert object back to Map
            this.event.slotsByDate = new Map(
              Object.entries(listingData.slotsByDate)
            );
          }

          // Set selected slots from eventSchedule or other source
          if (
            listingData.eventSchedule &&
            listingData.eventSchedule.length > 0
          ) {
            // Create a set of unique time slots
            const uniqueTimeSlots = new Set();

            for (const event of listingData.eventSchedule) {
              // Extract time information
              const dateObj = new Date(event.datetime);

              // Create time slot object similar to what generateTimeSlots returns
              const hours = dateObj.getUTCHours();
              const minutes = dateObj.getUTCMinutes();

              // Format time string
              const timeString = new Date(
                2000,
                0,
                1,
                hours,
                minutes
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });

              // Create slot object
              const slot = {
                timeString,
                date: new Date(2000, 0, 1, hours, minutes),
                eventHours: hours,
                eventMinutes: minutes,
                ampm: hours < 12 ? "AM" : "PM",
              };

              // Store in uniqueTimeSlots based on timeString
              if (!uniqueTimeSlots.has(timeString)) {
                uniqueTimeSlots.add(timeString);
                this.selectedSlots.push(slot);
              }
            }
          }

          // Update eligible days
          if (listingData.eligibleDays && listingData.eligibleDays.length > 0) {
            this.eligibleDays = listingData.eligibleDays.map((day) =>
              day.seconds ? new Date(day.seconds * 1000) : new Date(day)
            );
          }

          // Store eventSchedule
          this.eventSchedule = listingData.eventSchedule || [];

          // Migrate existing eventSchedule to include capacity fields if missing
          if (this.eventSchedule.length > 0) {
            this.eventSchedule = this.eventSchedule.map((event) => ({
              ...event,
              maxGuestCount:
                event.maxGuestCount || this.event.maxGuestCount || 4,
              currentGuestCount: event.currentGuestCount || 0,
            }));
          }
        }
      } catch (error) {
        console.error("Error loading listing data:", error);
      } finally {
        this.isLoading = false;
      }
    },
    formatTimezone(tz) {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "long",
      });
      const parts = formatter.formatToParts(new Date());
      const timeZonePart = parts.find((p) => p.type === "timeZoneName");
      return `${tz} (${timeZonePart.value})`;
    },
    handleTimezoneChange() {
      // Reset all time-related selections
      this.startDate = "";
      this.endDate = "";
      this.selectedSlots = [];
      this.event.slotsByDate = new Map();
      this.eligibleDays = [];

      // Reset repeat-related fields if repeat is enabled
      if (this.event.repeat) {
        this.event.selectedDays = [];
        this.event.frequency = "";
      }

      // Regenerate time slots for new timezone
      this.timeSlots = this.generateTimeSlots();

      // Update calendar
      this.updateCalenderAttributes();
    },
    generateSlotObject(timeSlot) {
      return {
        timeSlot,
        isAvailable: true,
        updatedAt: new Date(),
        isLocked: false,
      };
    },
    updateSlotsByDate() {
      this.eligibleDays.forEach((date) => {
        const dateString = date.toISOString().split("T")[0];
        const slotObjects = this.selectedSlots.map((slot) =>
          this.generateSlotObject(slot)
        );
        this.event.slotsByDate.set(dateString, slotObjects);
      });
    },
    generateTimeSlots() {
      const slots = [];
      let start = 0;
      const eventTimezone = this.event.timezone;

      while (start < 24 * 60) {
        const hours = Math.floor(start / 60);
        const minutes = start % 60;

        // Create a DateTime object in the event timezone
        const dateTime = DateTime.fromObject(
          {
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0,
          },
          { zone: eventTimezone }
        );

        // Format the time for display
        const localTimeString = dateTime.toLocaleString({
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        // Store the slot with the timezone-specific hours and minutes
        slots.push({
          timeString: localTimeString,
          // Create a Date object for compatibility with existing code
          date: dateTime.toJSDate(),
          // Store the actual hours and minutes in the event timezone
          eventHours: hours,
          eventMinutes: minutes,
          ampm: hours < 12 ? "AM" : "PM",
        });

        start += 30;
      }

      return slots;
    },
    addTimeSlot() {
      if (this.selectedTimeSlot) {
        if (!this.selectedSlots.includes(this.selectedTimeSlot)) {
          this.selectedSlots.push(this.selectedTimeSlot);
        }
        this.selectedTimeSlot = "";
      }
    },
    removeTimeSlot(slot) {
      this.selectedSlots = this.selectedSlots.filter((time) => time !== slot);
    },
    getSlotsForDate(date) {
      return this.event.slotsByDate.get(date) || [];
    },
    calculateEligibleDays() {
      const startDate = new Date(this.startDate);
      const endDate = new Date(this.endDate);
      const eligibleDays = [];

      if (this.event.repeat === false) {
        eligibleDays.push(startDate);
        this.eligibleDays = eligibleDays;
        return;
      }

      if (startDate > endDate) {
        console.error("Start date must be before end date.");
        return [];
      }

      const dayIndices = this.event.selectedDays.map((day) => {
        return this.daysOfWeek.indexOf(day);
      });

      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        if (dayIndices.includes(d.getDay())) {
          eligibleDays.push(new Date(d));
        }
      }

      this.eligibleDays = eligibleDays;
      this.updateCalenderAttributes();
    },
    updateCalenderAttributes() {
      this.attrs = [
        {
          key: "today",
          dot: {
            color: "#42b983",
          },
          dates: new Date(),
        },
        {
          key: "eligible",
          highlight: {
            color: "#ff9800",
            fillMode: "light",
          },
          dates: this.eligibleDays,
        },
      ];
    },
    getUTCOffset() {
      const offset = new Date().getTimezoneOffset();
      const sign = offset <= 0 ? "+" : "-";
      const pad = (num) => String(num).padStart(2, "0");
      const hours = pad(Math.floor(Math.abs(offset) / 60));
      return `UTC${sign}${hours}`;
    },
    convertToUTC12HourFormat(dateString) {
      const utcDate = new Date(dateString);
      const hours = utcDate.getUTCHours();
      const minutes = utcDate.getUTCMinutes();

      const period = hours >= 12 ? "PM" : "AM";
      const hour12 = hours % 12 || 12;

      return `${hour12.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")} ${period}`;
    },

    async convertData(data) {
      const updatedData = {};

      for (const key in data) {
        updatedData[key] = data[key].map((item) => ({
          ...item,
          timeSlot: {
            ...item.timeSlot,
            date: new Date(item.timeSlot.date).toISOString(),
            timeString: this.convertToUTC12HourFormat(item.timeSlot.date),
          },
        }));
      }

      return updatedData;
    },

    /**
     * Generates a UUID v4
     * @returns {string} A random UUID
     */
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },

    /**
     * Generates an event schedule with UTC datetimes
     * @returns {Array} Array of event objects with datetime, availability, etc.
     */
    generateEventSchedule() {
      const schedule = [];
      const eventTimezone = this.event.timezone;
      const maxGuestCount = this.event.maxGuestCount || 4;

      // For each eligible day
      this.eligibleDays.forEach((day) => {
        // For each selected time slot
        this.selectedSlots.forEach((timeSlot) => {
          // Get date components from the eligible day
          const eligibleDate = new Date(day);
          const year = eligibleDate.getFullYear();
          const month = eligibleDate.getMonth() + 1; // Month is 0-indexed in JS
          const dayOfMonth = eligibleDate.getDate();

          // Use the eventHours and eventMinutes stored directly in the time slot
          // These hours/minutes are already in the event timezone
          const hours = timeSlot.eventHours;
          const minutes = timeSlot.eventMinutes;

          console.log(
            `Creating slot for ${year}-${month}-${dayOfMonth} at ${hours}:${minutes} in ${eventTimezone}`
          );

          // Create DateTime in the event's timezone
          const eventDateTime = DateTime.fromObject(
            {
              year,
              month,
              day: dayOfMonth,
              hour: hours,
              minute: minutes,
              second: 0,
              millisecond: 0,
            },
            { zone: eventTimezone }
          );

          console.log(
            `Event time in ${eventTimezone}: ${eventDateTime.toISO()}`
          );

          // Convert to UTC
          const utcDateTime = eventDateTime.toUTC();
          console.log(`Converted to UTC: ${utcDateTime.toISO()}`);

          // Create the event object with UTC time and guest count fields
          const event = {
            datetime: utcDateTime.toISO(), // UTC format ISO string
            isAvailable: true,
            isLocked: false,
            slotId: this.generateUUID(),
            maxGuestCount: maxGuestCount,
            currentGuestCount: 0,
          };

          schedule.push(event);
        });
      });

      return schedule;
    },

    formatUTCDate(isoString) {
      return DateTime.fromISO(isoString, { zone: "UTC" }).toLocaleString({
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatUTCTime(isoString) {
      return DateTime.fromISO(isoString, { zone: "UTC" }).toLocaleString({
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    async handleSubmit() {
      // Make sure we have the selected date in eligibleDays for non-repeat events
      if (!this.event.repeat && this.startDate) {
        if (this.eligibleDays.length === 0) {
          // If eligibleDays is empty, add the startDate
          this.eligibleDays = [new Date(this.startDate)];
          console.log("Added single date to eligible days:", this.eligibleDays);
        }
      }

      // Check if we have dates and time slots
      if (this.eligibleDays.length === 0) {
        alert("Please select at least one date");
        return;
      }

      if (this.selectedSlots.length === 0) {
        alert("Please select at least one time slot");
        return;
      }

      // Generate the event schedule
      this.eventSchedule = this.generateEventSchedule();
      console.log("Generated schedule:", this.eventSchedule);

      // Show the debug modal
      this.showDebugModal = true;
    },

    async proceedWithSubmission() {
      // Close the modal
      this.showDebugModal = false;

      // Execute the submission logic
      const timezone = this.event.timezone;
      const sessionStore = useSessionStore();

      this.updateSlotsByDate();
      const slotsByDateObject = Object.fromEntries(this.event.slotsByDate);
      let utcSlotsByDate = await this.convertData(slotsByDateObject);

      const eventData = {
        ...this.event,
        creatorUserId: sessionStore.userId,
        startDate: this.startDate,
        endDate: this.event.repeat ? this.endDate : this.startDate,
        slotsByDate: utcSlotsByDate,
        eligibleDays: this.eligibleDays,
        eventSchedule: this.eventSchedule,
        timezone: timezone,
      };

      // If in edit mode, update the existing document
      if (this.isEditMode) {
        try {
          const listingRef = doc(db, "listings", this.listingId);

          // Add updatedAt timestamp
          eventData.updatedAt = new Date();

          // Keep the original createdAt date
          delete eventData.createdAt;

          await updateDoc(listingRef, eventData);
          this.$router.push({ name: "Listings" });
        } catch (error) {
          console.error("Error updating listing:", error);
          alert("Failed to update listing. Please try again.");
        }
      } else {
        // Create new document
        eventData.createdAt = new Date();
        console.log("Event data:", eventData);
        await addDoc(collection(db, "listings"), eventData);
        this.$router.push({ name: "Listings" });
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Create local preview immediately
      const localPreviewUrl = URL.createObjectURL(file);
      this.event.bannerUrl = localPreviewUrl;

      const metadata = {
        contentType: "image/jpeg",
      };

      const storageRef_ = storageRef(storage, "images/" + file.name);
      const uploadTask = uploadBytesResumable(storageRef_, file, metadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          this.uploadProgress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
        },
        (error) => {
          console.error("Upload error:", error);
          this.uploadProgress = 0;
          // Cleanup local preview URL on error
          URL.revokeObjectURL(localPreviewUrl);
          this.event.bannerUrl = null;
        },
        async () => {
          // Cleanup local preview URL
          URL.revokeObjectURL(localPreviewUrl);
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          this.event.bannerUrl = url;
          this.uploadProgress = 0;
        }
      );
    },
    removeBanner() {
      this.event.bannerUrl = null;
      this.uploadProgress = 0;
      // Reset the file input
      const fileInput = document.getElementById("bannerUrl");
      if (fileInput) fileInput.value = "";
    },
    clearSelection() {
      console.log("Clearing selection...");
      this.event.frequency = "";
      this.event.selectedDays = [];
      this.event.slotsByDate = new Map();
      this.endDate = "";
      this.calculateEligibleDays();
    },
    /**
     * Formats a date in a specific timezone using Luxon
     * @param {string|Date} date - The date to format (ISO string or Date object)
     * @param {string} timezone - The target timezone
     * @param {Object} options - DateTime format options
     * @returns {string} Formatted date string in the target timezone
     */
    formatDateInTimezone(date, timezone, options = {}) {
      // Convert date to ISO string if it's a Date object
      const isoString = date instanceof Date ? date.toISOString() : date;

      // Use Luxon to handle the timezone conversion and formatting
      return DateTime.fromISO(isoString)
        .setZone(timezone)
        .toLocaleString(options);
    },
    async handlePlaceSelect(event) {
      console.log("Event received:", event);

      try {
        // The event has placePrediction, we need to convert it to place
        const placeId = event.placePrediction.toPlace().id;

        if (!placeId) {
          console.error("No place ID found");
          return;
        }

        // Use Google Maps JavaScript API instead of direct fetch (avoids CORS issues)
        if (
          // eslint-disable-next-line no-undef
          typeof google === "undefined" ||
          // eslint-disable-next-line no-undef
          !google.maps ||
          // eslint-disable-next-line no-undef
          !google.maps.places
        ) {
          console.error("Google Maps API not loaded, waiting...");
          // Wait for Google Maps to load
          setTimeout(() => this.handlePlaceSelect(event), 1000);
          return;
        }

        // eslint-disable-next-line no-undef
        const service = new google.maps.places.PlacesService(
          document.createElement("div")
        );

        service.getDetails(
          {
            placeId: placeId,
            fields: ["geometry", "formatted_address", "name"],
          },
          (result, status) => {
            // eslint-disable-next-line no-undef
            if (
              // eslint-disable-next-line no-undef
              status === google.maps.places.PlacesServiceStatus.OK &&
              result
            ) {
              // Extract coordinates
              if (result.geometry && result.geometry.location) {
                this.event.latitude = result.geometry.location.lat();
                this.event.longitude = result.geometry.location.lng();
                console.log("Latitude:", this.event.latitude);
                console.log("Longitude:", this.event.longitude);
              } else {
                console.log("No geometry data found in Place Details response");
              }

              // Set location name
              this.event.location =
                result.formatted_address || result.name || "";
            } else {
              console.error("Place Details request failed:", status);
            }
          }
        );
      } catch (error) {
        console.error("Error handling place selection:", error);
      }
    },
  },
  mounted() {
    // The new Place Autocomplete Element handles initialization automatically
    // No manual setup needed in mounted()
  },
  watch: {
    startDate() {
      this.calculateEligibleDays();
    },
    endDate() {
      this.calculateEligibleDays();
    },
    "event.repeats": {
      handler() {
        this.clearSelection();
      },
      deep: true,
    },
    "event.selectedDays": {
      handler() {
        this.calculateEligibleDays();
      },
      deep: true,
    },
  },
  setup() {
    const attrs = ref([
      {
        key: "today",
        dot: {
          color: "#42b983",
        },
        dates: new Date(),
      },
    ]);
    return {
      attrs,
    };
  },
  computed: {
    convertedTimezoneSchedule() {
      if (!this.eventSchedule.length) return [];

      return this.eventSchedule.map((event) => {
        // Parse the UTC datetime using Luxon
        const utcDateTime = DateTime.fromISO(event.datetime, { zone: "UTC" });

        // Convert to the selected timezone
        const localDateTime = utcDateTime.setZone(this.testTimezone);

        return {
          ...event,
          localDate: localDateTime.toLocaleString({
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          localTime: localDateTime.toLocaleString({
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        };
      });
    },
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-align: center;
  overflow: hidden;
  margin-top: 5rem;
}

.left-container {
  flex: 1.5;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  overflow-y: auto;
  scrollbar-width: none;
}

.left-container::-webkit-scrollbar {
  display: none;
}

.right-container {
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

.listing-details {
  display: grid;
  margin: 20px;
  width: 75%;
  height: auto;
  margin-bottom: 400px;
}

.event-form {
  text-align: left;
  padding: 2rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

h2 {
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subheading {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  font-weight: normal;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #111827;
}

input[type="text"],
input[type="date"],
textarea,
select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #111827;
  background-color: #fff;
  transition: border-color 0.2s;
}

input[type="text"]:focus,
input[type="date"]:focus,
textarea:focus,
select:focus {
  outline: none;
  border-color: #000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
}

textarea {
  min-height: 100px;
  resize: vertical;
}

.selected-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.time-chip {
  display: inline-flex;
  align-items: center;
  background-color: #f3f4f6;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
}

.remove-chip {
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #374151;
}

.repeat-group {
  background-color: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.repeat-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.repeat-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  cursor: pointer;
}

.repeat-options {
  margin-top: 1rem;
}

.frequency-select,
.days-select {
  margin-bottom: 1rem;
}

.days {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.day-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.day-option input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
}

.banner-group {
  margin-top: 2rem;
}

.privacy-group {
  background-color: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.privacy-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.privacy-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  cursor: pointer;
}

.privacy-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.remove-banner {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  border-radius: 50%;
  transition: all 0.2s;
}

.remove-banner:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.upload-progress {
  margin: 1rem 0;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #000;
  transition: width 0.3s ease;
}

.progress-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.banner-preview {
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.banner-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-preview:hover {
  opacity: 0.8;
}

.file-input-wrapper {
  position: relative;
  width: 100%;
  height: 120px;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  cursor: pointer;
  transition: all 0.2s;
}

.file-input-wrapper:hover {
  border-color: #000;
  background-color: #f3f4f6;
}

.file-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.file-input-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
}

.file-input-placeholder span {
  font-size: 0.9rem;
}

.submit-button {
  width: 100%;
  padding: 0.875rem;
  background-color: #000;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 2rem;
}

.submit-button:hover {
  background-color: #111827;
}

.booking-container {
  position: fixed;
  top: 5rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  width: 380px;
  margin: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.booking-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.calendar-wrapper {
  padding: 1.5rem;
}

/* V-Calendar Overrides */
:deep(.vc-container) {
  border: none !important;
}

:deep(.vc-header) {
  padding: 0 !important;
  margin-bottom: 1rem;
}

:deep(.vc-weeks) {
  padding: 0 !important;
}

:deep(.vc-day) {
  padding: 0.25rem !important;
}

:deep(.vc-day-content) {
  font-size: 0.875rem !important;
  color: #111827 !important;
  font-weight: 400 !important;
}

:deep(.vc-day.is-today .vc-day-content) {
  background-color: #f3f4f6 !important;
  color: #000 !important;
}

:deep(.vc-day.is-selected .vc-day-content) {
  background-color: #000 !important;
  color: #fff !important;
}

.timezone-select {
  margin-bottom: 0.5rem;
}

.timezone-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Debug Modal Styles */
.debug-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.debug-modal-content {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 80%;
  max-width: 900px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.debug-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.debug-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #111827;
}

.close-modal-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-modal-button:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.debug-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.schedule-info {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
}

.schedule-info p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
  color: #374151;
}

.schedule-table {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.schedule-table table {
  width: 100%;
  border-collapse: collapse;
}

.schedule-table th,
.schedule-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.schedule-table th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #111827;
}

.schedule-table tr:hover td {
  background-color: #f9fafb;
}

.debug-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.modal-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.modal-button:not(.submit) {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-button:not(.submit):hover {
  background-color: #e5e7eb;
}

.modal-button.submit {
  background-color: #000;
  color: white;
}

.modal-button.submit:hover {
  background-color: #111827;
}

.timezone-test-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.table-title {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #111827;
}

.timezone-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.timezone-selector-wrapper label {
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
    margin-top: 2rem;
    padding: 0 1rem;
  }

  .left-container {
    flex: none;
    width: 100%;
    justify-content: center;
    margin-bottom: 2rem;
  }

  .right-container {
    flex: none;
    width: 100%;
    padding-bottom: 2rem;
    justify-content: center;
  }

  .listing-details {
    width: 100%;
    margin: 0;
    margin-bottom: 2rem;
  }

  .event-form {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
  }

  .subheading {
    font-size: 0.85rem;
  }

  /* Make form inputs more touch-friendly */
  input[type="text"],
  input[type="date"],
  input[type="number"],
  textarea,
  select {
    padding: 0.875rem;
    font-size: 1rem;
    min-height: 44px;
  }

  .submit-button {
    padding: 1rem;
    font-size: 1rem;
    min-height: 48px;
  }

  /* Fix calendar container for mobile */
  .booking-container {
    position: static;
    width: 100%;
    margin: 0;
    max-width: none;
  }

  .booking-header {
    padding: 1rem;
  }

  .calendar-wrapper {
    padding: 1rem;
  }

  /* Time chip improvements for mobile */
  .time-chip {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }

  .remove-chip {
    margin-left: 0.625rem;
    padding: 0.25rem;
  }

  /* Days grid for mobile */
  .days {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .day-option {
    gap: 0.75rem;
  }

  .day-option input[type="checkbox"] {
    width: 1.125rem;
    height: 1.125rem;
  }

  /* File upload for mobile */
  .file-input-wrapper {
    height: 100px;
  }

  .banner-preview {
    height: 150px;
  }

  /* Debug modal for mobile */
  .debug-modal-content {
    width: 95%;
    max-height: 90vh;
    margin: 1rem;
  }

  .debug-modal-header {
    padding: 1rem;
  }

  .debug-modal-body {
    padding: 1rem;
  }

  .schedule-table {
    font-size: 0.8rem;
  }

  .schedule-table th,
  .schedule-table td {
    padding: 0.5rem;
  }

  .debug-actions {
    flex-direction: column;
  }

  .modal-button {
    width: 100%;
    padding: 0.875rem;
  }

  .timezone-selector-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .timezone-selector-wrapper select {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 0.75rem;
    margin-top: 1rem;
  }

  .event-form {
    padding: 1rem;
  }

  h2 {
    font-size: 1.125rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .days {
    grid-template-columns: 1fr;
  }

  .selected-slots {
    gap: 0.375rem;
  }

  .time-chip {
    padding: 0.5rem 0.875rem;
    font-size: 0.85rem;
  }
}
</style>
